import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "container-fluid d-flex align-items-center flex-wrap gap-2 row py-2" }
const _hoisted_2 = { class: "col-12 col-md-auto text-secondary text-sm mb-1 mb-md-0" }
const _hoisted_3 = {
  key: 0,
  class: "text-muted fst-italic"
}
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "col-auto d-inline-flex align-items-center gap-2" }
const _hoisted_6 = { class: "text-secondary text-sm text-nowrap mb-0" }
const _hoisted_7 = ["value", "disabled"]
const _hoisted_8 = ["value"]
const _hoisted_9 = {
  "aria-label": "Page navigation",
  class: "col-auto"
}
const _hoisted_10 = { class: "pagination pagination-sm mb-0" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = {
  key: 0,
  class: "page-item disabled"
}
const _hoisted_13 = ["disabled", "onClick"]
const _hoisted_14 = ["disabled"]
const _hoisted_15 = { class: "col-auto d-inline-flex align-items-center gap-2" }
const _hoisted_16 = { class: "text-secondary text-sm text-nowrap mb-0" }
const _hoisted_17 = { class: "d-flex flex-column" }
const _hoisted_18 = ["max", "disabled"]
const _hoisted_19 = {
  key: 0,
  class: "invalid-feedback d-block",
  style: {"font-size":"0.7rem","white-space":"nowrap"}
}

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  total: number
  page: number
  perPage: number
  perPageOptions?: number[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PagePaginator',
  props: {
    total: {},
    page: {},
    perPage: {},
    perPageOptions: { default: () => [20, 30, 50] }
  },
  emits: ["update:page", "update:perPage"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n()

const props = __props

const emit = __emit

// ── Computed ──────────────────────────────────────────────────────────────────

const resolvedPerPageOptions = computed(() => props.perPageOptions)

const totalPages = computed(() => {
  if (props.total === 0 || props.perPage <= 0) return 1
  return Math.ceil(props.total / props.perPage)
})

const rangeFrom = computed(() => (props.page - 1) * props.perPage + 1)
const rangeTo = computed(() => Math.min(props.page * props.perPage, props.total))

/** Build the list of page numbers / ellipsis markers to render. */
const pageItems = computed((): (number | '...')[] => {
  const n = totalPages.value
  const p = props.page

  if (n <= 7) {
    return Array.from({ length: n }, (_, i) => i + 1)
  }

  // Page near the start — show leading window
  if (p <= 3) {
    return [1, 2, 3, '...', n]
  }

  // Page near the end — show trailing window
  if (p >= n - 2) {
    return [1, '...', n - 2, n - 1, n]
  }

  // General middle case
  return [1, '...', p - 1, p, p + 1, '...', n]
})

// ── Jump input ────────────────────────────────────────────────────────────────

const jumpInput = ref<number>(props.page)
const jumpError = ref(false)

watch(() => props.page, (val) => {
  jumpInput.value = val
  jumpError.value = false
})

function commitJump(): void {
  const val = jumpInput.value
  if (!Number.isInteger(val) || val < 1 || val > totalPages.value) {
    jumpError.value = true
    jumpInput.value = props.page // revert
    return
  }
  jumpError.value = false
  if (val !== props.page) {
    emit('update:page', val)
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function emitPage(p: number): void {
  if (p < 1 || p > totalPages.value || p === props.page) return
  emit('update:page', p)
}

function onPerPageChange(event: Event): void {
  const value = Number((event.target as HTMLSelectElement).value)
  emit('update:perPage', value)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (__props.total === 0)
        ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(t)('common.paginator.empty')), 1))
        : (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(_unref(t)('common.paginator.summary', { from: rangeFrom.value, to: rangeTo.value, total: __props.total })), 1))
    ]),
    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "col d-none d-md-block" }, null, -1)),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("label", _hoisted_6, _toDisplayString(_unref(t)('common.paginator.per_page')), 1),
      _createElementVNode("select", {
        class: "form-select form-select-sm",
        value: __props.perPage,
        disabled: __props.total === 0,
        onChange: onPerPageChange
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resolvedPerPageOptions.value, (opt) => {
          return (_openBlock(), _createElementBlock("option", {
            key: opt,
            value: opt
          }, _toDisplayString(opt), 9, _hoisted_8))
        }), 128))
      ], 40, _hoisted_7)
    ]),
    _createElementVNode("nav", _hoisted_9, [
      _createElementVNode("ul", _hoisted_10, [
        _createElementVNode("li", {
          class: _normalizeClass(["page-item", { disabled: __props.page <= 1 || __props.total === 0 }])
        }, [
          _createElementVNode("button", {
            class: "page-link",
            disabled: __props.page <= 1 || __props.total === 0,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emitPage(__props.page - 1))),
            type: "button",
            "aria-label": "Previous"
          }, "‹", 8, _hoisted_11)
        ], 2),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageItems.value, (item, idx) => {
          return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
            (item === '...')
              ? (_openBlock(), _createElementBlock("li", _hoisted_12, [...(_cache[3] || (_cache[3] = [
                  _createElementVNode("span", { class: "page-link" }, "…", -1)
                ]))]))
              : (_openBlock(), _createElementBlock("li", {
                  key: 1,
                  class: _normalizeClass(["page-item", { active: item === __props.page, disabled: __props.total === 0 }])
                }, [
                  _createElementVNode("button", {
                    class: "page-link cursor-pointer",
                    type: "button",
                    disabled: __props.total === 0,
                    onClick: ($event: any) => (emitPage(item as number))
                  }, _toDisplayString(item), 9, _hoisted_13)
                ], 2))
          ], 64))
        }), 128)),
        _createElementVNode("li", {
          class: _normalizeClass(["page-item", { disabled: __props.page >= totalPages.value || __props.total === 0 }])
        }, [
          _createElementVNode("button", {
            class: "page-link",
            disabled: __props.page >= totalPages.value || __props.total === 0,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (emitPage(__props.page + 1))),
            type: "button",
            "aria-label": "Next"
          }, "›", 8, _hoisted_14)
        ], 2)
      ])
    ]),
    _createElementVNode("div", _hoisted_15, [
      _createElementVNode("label", _hoisted_16, _toDisplayString(_unref(t)('common.paginator.go_to_page')), 1),
      _createElementVNode("div", _hoisted_17, [
        _withDirectives(_createElementVNode("input", {
          class: _normalizeClass(["form-control form-control-sm", { 'is-invalid': jumpError.value }]),
          style: {"width":"64px"},
          type: "number",
          min: "1",
          max: totalPages.value,
          disabled: __props.total === 0,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((jumpInput).value = $event)),
          onKeydown: _withKeys(commitJump, ["enter"]),
          onBlur: commitJump
        }, null, 42, _hoisted_18), [
          [
            _vModelText,
            jumpInput.value,
            void 0,
            { number: true }
          ]
        ]),
        (jumpError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, " 1–" + _toDisplayString(totalPages.value), 1))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})